import type { FunctionComponent } from 'react';
import { useState } from 'react';
import { ChevronsUpDown } from 'lucide-react';

import type { HighRiskTransaction } from './types';
import { TransactionDetailModal } from './TransactionDetailModal';

type HighRiskTransactionsProps = {
  transactions: HighRiskTransaction[];
};

export const HighRiskTransactions: FunctionComponent<HighRiskTransactionsProps> = ({
  transactions,
}) => {
  const [selectedTransaction, setSelectedTransaction] = useState<HighRiskTransaction | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleViewClick = (transaction: HighRiskTransaction) => {
    setSelectedTransaction(transaction);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setTimeout(() => setSelectedTransaction(null), 300);
  };

  if (!transactions || transactions.length === 0) {
    return null;
  }

  return (
    <>
      <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-md">
        <div className="mb-4">
          <h2 className="text-xl font-semibold" style={{ color: '#14203D' }}>
            Transaction Patterns Analysis
          </h2>
          <p className="mt-1 text-sm text-gray-600">
            High-risk transaction patterns requiring review
          </p>
        </div>
        <div className="overflow-hidden rounded-lg border-2 border-gray-200">
          <div className="flex border-b border-gray-200 bg-white">
            <div className="flex flex-1 items-center gap-2 px-4 py-3 text-left text-sm font-semibold text-[#787981]">
              Risk Indicator
              <ChevronsUpDown className="h-3 w-3" />
            </div>
            <div className="w-48 whitespace-nowrap px-4 py-3 text-sm font-semibold text-[#787981]">
              Reference Transactions
            </div>
          </div>
          <table className="w-full">
            <thead className="sr-only">
              <tr>
                <th>Risk Indicator</th>
                <th>Reference Transactions</th>
              </tr>
            </thead>
            <tbody>
              {transactions.map((transaction, index) => (
                <tr
                  key={`${transaction.label}-${transaction.reason}`}
                  className={`border-b border-gray-100 last:border-b-0 ${
                    index % 2 === 0 ? 'bg-white' : 'bg-gray-50'
                  }`}
                >
                  <td className="px-4 py-3 text-sm text-gray-700">{transaction.reason}</td>
                  <td className="w-48 py-3 text-center">
                    <button
                      onClick={() => handleViewClick(transaction)}
                      className="text-sm font-bold text-blue-600 hover:text-blue-800 hover:underline"
                    >
                      View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <TransactionDetailModal
        transaction={selectedTransaction}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
      />
    </>
  );
};
